import { type ReactNode, type Ref } from "react";
import { keyframes, styled } from "@pigment-css/react";
import * as Dialog from "@radix-ui/react-dialog";
import { CrossLarge } from "@acto/web-common/components";
import { DimOverlay } from "@acto/taskpane/field-sales-2/components/primitives/DimOverlay";
import { useBodyScrollLock } from "@acto/taskpane/field-sales-2/components/primitives/useBodyScrollLock";
import {
  HeaderIconButton,
  HEADER_ICON_BUTTON_WIDTH,
} from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { useKeyboardInset } from "./useKeyboardInset";

const slideUp = keyframes({
  from: { opacity: 0, transform: "translateY(100%)" },
  to: { opacity: 1, transform: "translateY(0)" },
});

const slideDown = keyframes({
  from: { opacity: 1, transform: "translateY(0)" },
  to: { opacity: 0, transform: "translateY(100%)" },
});

const fadeIn = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });
const fadeOut = keyframes({ from: { opacity: 1 }, to: { opacity: 0 } });

/** Sheet stacking layer. Popovers portaled out of a sheet must sit above this. */
export const SHEET_Z_INDEX = 1101;

const Content = styled(Dialog.Content)(({ theme }) => ({
  position: "fixed",
  zIndex: SHEET_Z_INDEX,
  left: theme.fieldSalesTwo.spacing[8],
  right: theme.fieldSalesTwo.spacing[8],
  bottom: `calc(${theme.fieldSalesTwo.spacing[14]} + var(--keyboard-inset, 0px))`,
  maxWidth: "450px",
  maxHeight: `calc(100dvh - ${theme.fieldSalesTwo.spacing[64]} - var(--keyboard-inset, 0px))`,
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[8],
  paddingBottom: theme.fieldSalesTwo.spacing[12],
  background: theme.fieldSalesTwo.colors.background,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  borderRadius: `${theme.fieldSalesTwo.radius[38]} ${theme.fieldSalesTwo.radius[38]} 48px 48px`,
  overflow: "hidden",
  outline: "none",
  fontSize: theme.fieldSalesTwo.font.size.base,
  willChange: "transform, opacity",
  // WebKit/Chromium can fail to clip composited descendants (the scrollable Body, the
  // sticky CTA, the backdrop-filter close button) to a rounded `overflow: hidden`
  // ancestor while that ancestor is itself promoted (willChange/animation) — their
  // square corners then poke past the sheet's radius. Masking to the (rounded)
  // border-box forces the clip; both stops are opaque so there's no visual change.
  maskImage: "linear-gradient(#000, #000)",
  WebkitMaskImage: "linear-gradient(#000, #000)",
  '&[data-state="open"]': {
    animation: `${slideUp} ${theme.fieldSalesTwo.motion.duration.sheetIn} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  },
  '&[data-state="closed"]': {
    animation: `${slideDown} ${theme.fieldSalesTwo.motion.duration.sheetOut} ${theme.fieldSalesTwo.motion.easing.standard}`,
    pointerEvents: "none",
  },
  "@media (prefers-reduced-motion: reduce)": {
    '&[data-state="open"]': {
      animation: `${fadeIn} ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
    },
    '&[data-state="closed"]': {
      animation: `${fadeOut} ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
    },
  },
}));

const FullScreenContent = styled(Content)({
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  maxWidth: "none",
  maxHeight: "none",
  margin: 0,
  paddingBottom: 0,
  border: "none",
  borderRadius: 0,
});

// Overlaid on the top of the scrolling Body (not a flex row above it) so content can
// scroll beneath it, matching the page TopBar. The gradient is opaque (sheet bg) at the
// top and fades to transparent; the frost blurs whatever content slides under that zone.
const Header = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 2,
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[10]}`,
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  background: `linear-gradient(to bottom, ${theme.fieldSalesTwo.colors.background} 34.6%, rgba(240,238,237,0) 100%)`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  // The fade zone must not swallow scroll/taps meant for the content beneath it; only
  // the actual controls (close button, title) stay interactive.
  pointerEvents: "none",
  "& > *": {
    pointerEvents: "auto",
  },
  "&::after": {
    content: '""',
    width: HEADER_ICON_BUTTON_WIDTH,
    flexShrink: 0,
    // The spacer balances the centered title; it must never intercept taps meant for
    // the content scrolling beneath the header.
    pointerEvents: "none",
  },
  '&[data-has-action="true"]::after': {
    content: "none",
  },
}));

const Body = styled("div")(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
  display: "flex",
  flexDirection: "column",
  // Header is absolutely positioned over the top of this scroll area; pad the top by the
  // header's height (24 + 48 close button + 10 + the 8 that the flex gap used to add) so
  // the first content lands just below it.
  paddingTop: `calc(${theme.fieldSalesTwo.spacing[24]} + ${theme.fieldSalesTwo.spacing[48]} + ${theme.fieldSalesTwo.spacing[10]} + ${theme.fieldSalesTwo.spacing[8]})`,
}));

const HeaderText = styled("div")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[4],
}));

const HeaderSubtitle = styled("span")(({ theme }) => ({
  maxWidth: "100%",
  // The ellipsis needs `overflow: hidden`, which clips whatever leaves the line box, so the
  // line box must be tall enough to hold ascenders and descenders (`lineHeight: 1` cuts them).
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const HeaderTitle = styled(Dialog.Title)(({ theme }) => ({
  margin: 0,
  flex: 1,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  textAlign: "center",
  transition: `opacity ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}, transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  '&[data-hidden="true"]': {
    opacity: 0,
    transform: `translateY(${theme.fieldSalesTwo.spacing[6]})`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

/** Header capsule button (close-button shell); extend it for header actions like a delete toggle. */
export const SheetHeaderButton = styled(HeaderIconButton)(({ theme }) => ({
  "& svg": {
    width: theme.fieldSalesTwo.spacing[18],
    height: theme.fieldSalesTwo.spacing[18],
  },
}));

// Reference-counted so stacked sheets (e.g. the create-todo form + its customer picker) don't clear
// the shared `data-sheet-open` flag / scroll lock when only the inner one closes — they drop only
// once the last open sheet unmounts.
type SheetVariant = "anchored" | "fullScreen";

interface BottomSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  closeAriaLabel: string;
  children: ReactNode;
  /** `fullScreen` fills the pane for tall content; `anchored` (default) is the bottom card. */
  variant?: SheetVariant;
  /** Header button icon. Defaults to a close cross; pass e.g. <ChevronLeft /> for a back affordance. */
  closeIcon?: ReactNode;
  /** Ref to the scrollable Body element — lets content compensate scroll while it expands inline
   * (e.g. CreateTodoSheet pins the scroll to the bottom so its calendar grows upward). */
  bodyRef?: Ref<HTMLDivElement>;
  /** Rendered top-right in the header. */
  headerAction?: ReactNode;
  /** Hides the title visually only; it stays in the DOM as the dialog's accessible name (Radix). */
  titleHidden?: boolean;
}

export function BottomSheet({
  isOpen,
  onOpenChange,
  title,
  subtitle,
  closeAriaLabel,
  children,
  variant = "anchored",
  closeIcon,
  bodyRef,
  headerAction,
  titleHidden,
}: BottomSheetProps) {
  useBodyScrollLock(isOpen);

  const Surface = variant === "fullScreen" ? FullScreenContent : Content;

  // Anchored sheets sit at the bottom, so the keyboard covers them; the full-screen variant fills
  // the pane and scrolls its own content, so it doesn't need the lift.
  const keyboardInsetRef = useKeyboardInset(isOpen && variant !== "fullScreen");

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {variant !== "fullScreen" && <DimOverlay />}
        <Surface aria-describedby={undefined} ref={keyboardInsetRef}>
          <Header data-has-action={headerAction ? "true" : undefined}>
            <SheetHeaderButton type="button" aria-label={closeAriaLabel} onClick={() => onOpenChange(false)}>
              {closeIcon ?? <CrossLarge />}
            </SheetHeaderButton>
            <HeaderText>
              <HeaderTitle data-hidden={titleHidden ? "true" : undefined}>{title}</HeaderTitle>
              {subtitle && <HeaderSubtitle>{subtitle}</HeaderSubtitle>}
            </HeaderText>
            {headerAction}
          </Header>
          <Body ref={bodyRef}>{children}</Body>
        </Surface>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function FullScreenSheet(props: Omit<BottomSheetProps, "variant">) {
  return <BottomSheet {...props} variant="fullScreen" />;
}
